import { Runtype, Array, Number, Object, Static, String, Boolean, Null, InstanceOf, Undefined } from 'runtypes';

const DateType = InstanceOf(Date);

const makeRoutes = () => ({
  getRootInstitutions: {
    method: 'get',
    path: '/root-institutions',
    requestBody: Undefined,
    responseData: RootInstitutionsRecord,
  },

  getInstitution: {
    method: 'get',
    path: '/institution/:institutionId',
    requestBody: Undefined,
    responseData: InstitutionRecord,
  },
  getInstitutionChildren: {
    method: 'get',
    path: '/institution/:institutionId/children',
    requestBody: Undefined,
    responseData: RootInstitutionsRecord,
  },
  getInstitutionClassesWithTimes: {
    method: 'get',
    path: '/institution/:institutionId/classes-times',
    requestBody: Undefined,
    responseData: ClassesWithTimesRecord(DateType),
  },
  getClassWithTimes: {
    method: 'get',
    path: '/class/:classId/times',
    requestBody: Undefined,
    responseData: ClassWithTimesRecord(DateType),
  },
  getInstitutionClassesWithUsers: {
    method: 'get',
    path: '/institution/:institutionId/classes-users',
    requestBody: Undefined,
    responseData: ClassesWithUsersRecord(DateType),
  },
  // List of contracts associated with any ancestor
  // or descendant of an institution, both expired and current
  getInstitutionContracts: {
    method: 'get',
    path: '/institution/:institutionId/contracts',
    requestBody: Undefined,
    responseData: InstitutionContractsRecord(DateType),
  },
  getInstitutionLicenseUsage: {
    method: 'get',
    path: '/institution/:institutionId/license-usage',
    requestBody: Undefined,
    responseData: InstitutionLicenseUsageRecord,
  },

  deleteInstitution: {
    method: 'delete',
    path: '/institution/:institutionId',
    requestBody: Undefined,
    responseData: Object({}),
  },

  deleteClass: {
    method: 'delete',
    path: '/class/:classId',
    requestBody: Undefined,
    responseData: Object({}),
  },

  deleteUser: {
    method: 'delete',
    path: '/user/:userId',
    requestBody: Undefined,
    responseData: Object({}),
  },
});

export const TeacherRecord = Object({
  id: Number,
  name: String.or(Null),
  firstName: String.or(Null),
  middleName: String.or(Null),
  lastName: String.or(Null),
});

const StudentRecord = TeacherRecord.and(
  Object({
    licenseStatus: String.or(Null),
    contractId: Number.or(Null),
  }),
);

export const ClassWithUsersRecord = <T extends Runtype>(useDate: T) =>
  Object({
    id: Number,
    externalName: String,
    nickName: String.or(Null),
    section: String.or(Null),
    active: Boolean,
    students: Array(StudentRecord),
    teachers: Array(TeacherRecord),
    createdAt: useDate,
  });

export const ClassWithTimesRecord = <T extends Runtype>(useDate: T) =>
  Object({
    id: Number,
    externalName: String,
    nickName: String.or(Null),
    section: String.or(Null),
    active: Boolean,
    createdAt: useDate,
    startAt: useDate.or(Null),
    endAt: useDate.or(Null),
  });

export const InstitutionRecord = Object({
  id: Number,
  externalName: String,
  nickName: String.or(Null),
  level: String.or(Null),
  children: Array(Number),
  classes: Array(Number),
  state: String,
});

export const TimingRecord = Object({
  timing: Array(
    Object({
      name: String,
      value: Number.or(Array(Number)),
    }),
  ).optional(),
  plans: Array(Object({})).optional(),
});

export const RootInstitutionsRecord = Object({
  institutions: Array(InstitutionRecord),
}).and(TimingRecord);

export const ClassesWithTimesRecord = <T extends Runtype>(useDate: T) =>
  Object({
    classes: Array(ClassWithTimesRecord(useDate)),
  }).and(TimingRecord);

export const ClassesWithUsersRecord = <T extends Runtype>(useDate: T) =>
  Object({
    classes: Array(ClassWithUsersRecord(useDate)),
  }).and(TimingRecord);

export const ContractRecord = <T extends Runtype>(useDate: T) =>
  Object({
    id: Number,
    institutionId: Number,
    licensesIssued: Number,
    licenseType: String,
    contractStartDate: useDate,
    contractEndDate: useDate.or(Null),
    consumed: Number.or(Null),
    reserved: Number.or(Null),
    unreserved: Number,
    enforce: String,
  });
export const InstitutionContractsRecord = <T extends Runtype>(useDate: T) =>
  Object({
    institutionId: Number,
    contracts: Array(ContractRecord(useDate)),
  });
export const LicenseUsageRecord = Object({
  consumed: Number,
  reserved: Number,
  unreserved: Number,
  classes: Number,
  students: Number,
  teachers: Number,
  otherContracts: Number,
});
export const InstitutionLicenseUsageRecord = Object({
  institutionId: Number,
  filter: Array(Number).or(Null),
  licenseUsage: LicenseUsageRecord,
});

export const InstitutionsRequestRecord = Object({});
export const InstitutionsResponseRecord = <T extends Runtype>(useDate: T) =>
  Array(
    Object({
      id: Number,
      parentId: Number.or(Null),
      name: String,
      state: String,
      level: String.or(Null),
      createdAt: useDate,
    }),
  );

export const ClassWithUsersRS = ClassWithUsersRecord(String);
export type ClassWithUsers = Static<typeof ClassWithUsersRS>;
const ttDate = ClassWithUsersRecord(DateType);
export type ClassWithUsersDate = Static<typeof ttDate>;
export const ClassWithTimesRS = ClassWithTimesRecord(String);
export type ClassWithTimes = Static<typeof ClassWithTimesRS>;
const uuDate = ClassWithTimesRecord(DateType);
export type ClassWithTimesDate = Static<typeof uuDate>;
export type Student = Static<typeof StudentRecord>;
export type Teacher = Static<typeof TeacherRecord>;
export type Institution = Static<typeof InstitutionRecord>;
export const ContractRS = ContractRecord(String);
export type Contract = Static<typeof ContractRS>;
export type LicenseUsage = Static<typeof LicenseUsageRecord>;
export const InstitutionContractsRS = InstitutionContractsRecord(String);
export type InstitutionContracts = Static<typeof InstitutionContractsRS>;
export type InstitutionLicenseUsage = Static<typeof InstitutionLicenseUsageRecord>;
export const InstitutionsResponseRS = InstitutionsResponseRecord(String);
export type InstitutionsResponse = Static<typeof InstitutionsResponseRS>;
export type InstitutionsRequest = Static<typeof InstitutionsRequestRecord>;
export type RootInstitutions = Static<typeof RootInstitutionsRecord>;
export const ClassesWithTimesRS = ClassesWithTimesRecord(String);
export type ClassesWithTimes = Static<typeof ClassesWithTimesRS>;
const yyDate = ClassesWithTimesRecord(DateType);
export type ClassesWithTimesDate = Static<typeof yyDate>;
export const ClassesWithUsersRS = ClassesWithUsersRecord(String);
export type ClassesWithUsers = Static<typeof ClassesWithUsersRS>;
const zzDate = ClassesWithUsersRecord(DateType);
export type ClassesWithUsersDate = Static<typeof zzDate>;

export const accountManagementRoutes = () => makeRoutes();
